import type { CityConfig } from "@/pages/city-template/types";

export const mainLineConfig: CityConfig = {
  city: "Main Line",
  state: "Pennsylvania",
  stateAbbr: "PA",
  slug: "main-line",
  route: "/cities/main-line",
  title: "Emergency Passport Main Line, PA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services on the Philadelphia Main Line — Ardmore, Bryn Mawr, Wayne, Radnor, Haverford. As fast as 24 hours. Serving Montgomery and Delaware County suburbs. A+ BBB rated registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services on the Main Line, Pennsylvania",
  heroSubtitle: "Business trip to London from PHL and your passport just expired? Villanova or Bryn Mawr student with a study abroad deadline? Fast Passport Center helps Main Line residents get passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Philadelphia%20Main%20Line%20suburban%20town%20with%20elegant%20stone%20buildings%20and%20tree-lined%20streets%2C%20affluent%20neighborhood%20with%20classic%20architecture%2C%20Bryn%20Mawr%20and%20Ardmore%20historic%20district%20atmosphere%2C%20green%20leafy%20suburban%20landscape%2C%20warm%20afternoon%20golden%20light%2C%20upscale%20Pennsylvania%20town%20charm%2C%20photorealistic%20aerial%20neighborhood%20photography%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=main-line-hero-01&orientation=landscape",
  heroImageAlt: "Philadelphia Main Line suburban streets with elegant architecture and tree-lined neighborhoods",
  geoPosition: "40.0231;-75.3416",
  geoPlacename: "Main Line, Pennsylvania",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "200 Chestnut St, Philadelphia, PA 19106",
    distance: "About 15–25 miles east — 30–45 minutes by car",
  },
  nearbyCitiesZips: [
    { name: "Ardmore", zips: "19003" },
    { name: "Bryn Mawr", zips: "19010" },
    { name: "Wayne", zips: "19087" },
    { name: "Radnor", zips: "19087" },
    { name: "Haverford", zips: "19041" },
    { name: "Villanova", zips: "19085" },
    { name: "Devon", zips: "19333" },
    { name: "Berwyn", zips: "19312" },
    { name: "Paoli", zips: "19301" },
    { name: "Malvern", zips: "19355" },
    { name: "Narberth", zips: "19072" },
    { name: "Bala Cynwyd", zips: "19004" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Main Line home or Villanova dorm in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from the Main Line", desc: "FedEx and UPS locations throughout Ardmore, Bryn Mawr, Wayne, and the entire Main Line offer overnight shipping." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission — critical for Villanova, Bryn Mawr, and Haverford students with study abroad deadlines." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the Philadelphia Passport Agency — just 30 minutes away, but no appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Elizabeth C.",
      location: "Bryn Mawr, PA",
      rating: 5,
      text: "My daughter needed a passport for a Bryn Mawr College semester abroad in London. We had less than three weeks. Fast Passport Center got her passport in 6 days. The process was seamless from start to finish.",
      service: "New Passport",
    },
    {
      name: "James H.",
      location: "Wayne, PA",
      rating: 5,
      text: "Business trip to Tokyo came up on five days' notice. My passport had expired and I was in full panic mode. Fast Passport Center handled it — renewed in 4 days. Shipped from the Radnor FedEx. Absolute lifesaver.",
      service: "Passport Renewal",
    },
    {
      name: "Margaret D.",
      location: "Ardmore, PA",
      rating: 5,
      text: "We planned a family trip to Greece and realized our son's passport was still in his old name after adoption. Fast Passport Center guided us through the name change and new passport. Everything arrived in 7 days. Wonderful.",
      service: "Name Change Passport",
    },
  ],
  facilities: [
    {
      id: "ardmore-post",
      name: "Ardmore Post Office",
      type: "USPS Passport Acceptance",
      address: "30 Ardmore Ave",
      city: "Ardmore",
      state: "PA",
      zip: "19003",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Ardmore — central Main Line",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance on the Main Line. Appointment required — book online well in advance. Photo services available.",
      icon: "ri-mail-line",
    },
    {
      id: "wayne-post",
      name: "Wayne Post Office",
      type: "USPS Passport Acceptance",
      address: "220 S Wayne Ave",
      city: "Wayne",
      state: "PA",
      zip: "19087",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Wayne — western Main Line",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Wayne option for western Main Line residents — Radnor, Devon, Berwyn area.",
      icon: "ri-mail-line",
    },
    {
      id: "bryn-mawr-post",
      name: "Bryn Mawr Post Office",
      type: "USPS Passport Acceptance",
      address: "16 N Bryn Mawr Ave",
      city: "Bryn Mawr",
      state: "PA",
      zip: "19010",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Bryn Mawr — near Bryn Mawr College",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Convenient Bryn Mawr location for college students and Haverford area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "radnor-township",
      name: "Radnor Township Building",
      type: "Municipal Passport Acceptance",
      address: "301 Iven Ave",
      city: "Wayne",
      state: "PA",
      zip: "19087",
      phone: "610-688-5600",
      hours: "Mon–Fri 8:30am–4:30pm",
      distance: "Wayne — Radnor Township",
      website: "https://www.radnor.com/",
      websiteLabel: "Radnor Township Website",
      notes: "Radnor Township may offer passport acceptance. Call ahead to confirm hours and appointment requirements.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport on the Main Line before international travel?",
      a: "Yes. Through Fast Passport Center, Main Line travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport on the Main Line?",
      a: "Fast passport renewal for Main Line residents is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years.",
    },
    {
      q: "Can Villanova and Bryn Mawr students get expedited passports for study abroad?",
      a: "Absolutely. We help Villanova University, Bryn Mawr College, Haverford College, and other Main Line college students get passports fast for study abroad programs. Whether it's a first-time passport or a renewal, we guide you through the process and can meet even tight semester deadlines.",
    },
    {
      q: "How close is the Philadelphia Passport Agency to the Main Line?",
      a: "The Philadelphia Passport Agency is only 15–25 miles from the Main Line — about 30–45 minutes by car. However, it still requires appointments and proof of travel within 14 days. Fast Passport Center is the more convenient option — start online and avoid the trip entirely.",
    },
    {
      q: "Can I expedite a child passport on the Main Line?",
      a: "Yes. Child passport expediting on the Main Line is possible through Fast Passport Center. Child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist. Once the acceptance step is complete, we handle expedited processing.",
    },
    {
      q: "What airports do Main Line travelers typically use?",
      a: "Philadelphia International Airport (PHL — about 20 miles southeast, 30 minutes drive) is the primary airport for Main Line travelers, offering extensive domestic and international flights. The Main Line's proximity to PHL makes international travel extremely convenient.",
    },
    {
      q: "How long does a standard passport take on the Main Line?",
      a: "If you apply through a standard post office acceptance facility on the Main Line, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner, you need expedited processing through Fast Passport Center.",
    },
    {
      q: "Can I get a passport renewal on the Main Line without visiting an office?",
      a: "Yes. Most Main Line adults renewing a passport can do so entirely by mail — no in-person visit required. Start your order online, complete documents at home, and ship overnight from any FedEx or UPS location in Ardmore, Bryn Mawr, Wayne, or surrounding Main Line communities.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Ardmore", address: "100 Lancaster Ave, Ardmore, PA 19003", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Bryn Mawr", address: "1218 W Lancaster Ave, Bryn Mawr, PA 19010", hours: "Daily 8 AM – 9 PM" },
    { name: "The UPS Store — Wayne", address: "215 W Lancaster Ave, Wayne, PA 19087", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 3 PM" },
    { name: "FedEx Office — King of Prussia", address: "160 N Gulph Rd, King of Prussia, PA 19406", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Rite Aid — Haverford", address: "600 W Lancaster Ave, Haverford, PA 19041", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Norristown PennDOT Driver License Center",
      address: "1700 Markley St, Norristown, PA 19401",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Norristown PennDOT center serving the Main Line area. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Malvern PennDOT Photo License Center",
      address: "225 Lancaster Ave, Malvern, PA 19355",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Malvern PennDOT option for western Main Line residents — Paoli, Malvern, Berwyn area.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "About 20 miles southeast — 30 minutes drive",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "PHL is the primary airport for Main Line travelers, offering extensive domestic and international flights. Passport required for all international travel.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Airport Website",
      featured: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PHL in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from the Main Line and Philadelphia suburbs.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Main Line residents renew fast — no office visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS on the Main Line", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Main Line travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Main Line families — minors under 16 require in-person acceptance and parental consent.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Main Line residents and college students at Villanova, Bryn Mawr, and Haverford.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all expedited.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Main Line, Pennsylvania travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Main Line residents in Montgomery and Delaware counties needing passports faster than standard timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Main Line, PA residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Main Line travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Main Line families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving the Philadelphia Main Line, Pennsylvania." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Main Line travelers often assume being close to Philadelphia means faster processing — it doesn't. Government processing still takes 6–13 weeks. Apply as soon as you book your trip." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and catch these issues before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. We tell you exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Assuming the Philadelphia Agency Has Open Appointments", description: "Philadelphia Passport Agency appointments fill up fast — sometimes weeks out. Don't count on a last-minute slot. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your travel date from PHL. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from the Main Line", description: "Drop off at any FedEx or UPS in Ardmore, Bryn Mawr, Wayne, or the Main Line area. Most locations are open evenings." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins", "Life-or-death emergencies only", "Must travel to Philadelphia (30–45 min)", "Expect to wait all day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots in Philadelphia", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Philadelphia (30–45 min each way)", fpc: "Ship documents overnight from the Main Line" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled", fpc: "Predictable processing based on option" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Philadelphia", route: "/cities/philadelphia" },
    { name: "Fast passport renewal in King of Prussia", route: "/cities/king-of-prussia" },
    { name: "Emergency passport help for Main Line travelers", route: "/states/pennsylvania" },
    { name: "Rush passport service near Villanova", route: "/cities/norristown" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location on the Main Line — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving the entire Main Line — Ardmore, Bryn Mawr, Wayne, Radnor, Haverford, Villanova, Devon, Berwyn, Paoli, Malvern, and Narberth",
  serviceAreaIntro: "Whether you are in Ardmore, Bryn Mawr, Wayne, Villanova, Radnor, Haverford, or anywhere along the Philadelphia Main Line, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Main Line communities and zip codes. From Narberth to Malvern — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport on the Main Line as simple as possible. Most Main Line clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Main Line travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Bryn Mawr, Wayne, and Ardmore travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Main Line travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/pennsylvania",
  statePageName: "Pennsylvania",
  dmvWebsite: "https://www.dmv.pa.gov/",
  dmvLabel: "PennDOT — Official Website",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from Pennsylvania Department of Health Vital Records for Montgomery and Delaware Counties.", url: "https://www.health.pa.gov/topics/certificates/Pages/Birth-Certificates.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Pennsylvania Department of Health Vital Records.", url: "https://www.health.pa.gov/topics/certificates/Pages/Death-Certificates.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Montgomery or Delaware County Marriage License Bureau.", url: "https://www.montgomerycountypa.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Montgomery or Delaware County Prothonotary.", url: "https://www.montgomerycountypa.gov/" },
    { icon: "ri-global-line", label: "VitalChek", desc: "Order Pennsylvania birth, death, marriage, and divorce certificates online through VitalChek for convenience.", url: "https://www.vitalchek.com/" },
  ],
};